import React, { useCallback } from 'react';
import { Select, useMultipleSelect, Typography } from '@vtblife/uikit';
import { OptionType, SelectMode, SelectProps } from '@vtblife/uikit/legacy';

import { LandStatus } from '@search/filter-enums/enums';

import { LandStatusFilter } from '../../../../../filters/models/Filter';

interface IDesktopLandStatusFilterViewProps {
    filter: LandStatusFilter;
    name?: string;
    onChange: (filter: LandStatusFilter) => void;
    defaultPlaceholder?: React.ReactElement;
    size?: 's' | 'm' | 'l';
}

enum ClientLandStatus {
    LPH,
    SNTDNP,
    IZHS
}

type OptionTypeLand = OptionType<ClientLandStatus> & { description: string };

const options: OptionTypeLand[] = [ {
    value: ClientLandStatus.LPH,
    label: 'ЛПХ',
    // eslint-disable-next-line max-len
    description: 'Личное подсобное хозяйство\u00A0— можно строить гаражи, вспомогательные конструкции и\u00A0строения для животных.'
}, {
    value: ClientLandStatus.SNTDNP,
    label: 'СНТ, ДНП',
    // eslint-disable-next-line max-len
    description: 'Садовое некоммерческое товарищество, дачное некоммерческое партнёрство\u00A0— можно строить садовые дома, сложно зарегистрироваться.'
}, {
    value: ClientLandStatus.IZHS,
    label: 'ИЖС',
    // eslint-disable-next-line max-len
    description: 'Индивидуальное жилое строительство\u00A0— можно построить дом для постоянного проживания\u00A0и зарегистрироваться в\u00A0нём.'
} ];

function getValues(filter?: LandStatusFilter): ClientLandStatus[] {
    const values: ClientLandStatus[] = [];

    if (filter?.landStatuses.includes(LandStatus.IZHS)) {
        values.push(ClientLandStatus.IZHS);
    }

    const isSntDnp = ! values.includes(ClientLandStatus.SNTDNP) &&
        (filter?.landStatuses.includes(LandStatus.SNT) || filter?.landStatuses.includes(LandStatus.DNP));

    if (isSntDnp) {
        values.push(ClientLandStatus.SNTDNP);
    }

    if (filter?.landStatuses.includes(LandStatus.AGRICULTURAL)) {
        values.push(ClientLandStatus.LPH);
    }

    return values;
}

export const DesktopLandStatusFilterView: React.FC<IDesktopLandStatusFilterViewProps> = ({
    filter,
    name = 'filter-land-status',
    onChange,
    defaultPlaceholder = <>Статус участка</>,
    size = 'm'
}) => {
    const handleChange = useCallback<
        NonNullable<SelectProps<OptionTypeLand, SelectMode.Multiple>['onChange']>
    >(values => {
        const result: LandStatus[] = [];

        values.forEach(value => {
            if (value === ClientLandStatus.IZHS) {
                result.push(LandStatus.IZHS);
                return;
            }
            if (value === ClientLandStatus.LPH) {
                result.push(LandStatus.AGRICULTURAL);
            }
            if (value === ClientLandStatus.SNTDNP) {
                result.push(LandStatus.DNP);
                result.push(LandStatus.SNT);
            }
        });
        onChange(new LandStatusFilter(result));
    }, [ onChange ]);

    const values = getValues(filter);

    const {
        handleChange: handleChangeSelect,
        is,
        selectedText
    } = useMultipleSelect<ClientLandStatus>(
        handleChange,
        {
            selected: values,
            texts: options.map(({ label, value }) => ({ value, text: label }))
        }
    );

    return (
        <Select mode='multiple' onChange={handleChangeSelect}>
            <Select.Button size={size} dataTest={name + '-trigger'}>
                {values.length > 0 ? selectedText : defaultPlaceholder}
            </Select.Button>
            <Select.Content>
                {options.map(({ value, label, description }) => (
                    <Select.Option
                        key={value}
                        selected={is(value)}
                        value={value}
                    >
                        <div style={{ width: 420 }}>
                            {label}
                            <Typography variant='small-alone' color='black-300'>
                                {description}
                            </Typography>
                        </div>
                    </Select.Option>
                ))}
            </Select.Content>
        </Select>
    );
};
